<script setup lang="ts">
/**
 * Step 1 du checkout adhésion :
 *  - sélecteur(s) de quantité par plan (mode multi-plan)
 *  - ou plan unique + tarif libre éventuel
 *  - options + custom fields inline (single member uniquement)
 *  - récap + bouton Continuer
 */
import { nomDuTarif, saisonDuTarifTerminee, tarifAdhesionFerme } from "~/utils/tarifAdhesion";
import { stripHtml, truncateForMeta } from "@donoor/utils/string";
import type {
  MembershipPlanPublic,
  MembershipPlanOption,
  MembershipCustomField,
} from "~/types/membership";

const { formatCurrency } = useFormatLangue();

// Aperçu texte d'une description de plan (line-clamp visuel + coupe sûre) :
// stripHtml décode les entités, truncateForMeta ne coupe jamais un emoji en
// deux (pas de « ￯ » orphelin).
const planDescriptionPreview = (html: string): string =>
  truncateForMeta(stripHtml(html), 120);

interface ParticipantLocal {
  planId: string;
  planTitle: string;
  planPrice: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  selectedOptions: Record<string, boolean>;
  customFieldValues: Record<string, unknown>;
}

type SiblingPlan = MembershipPlanPublic & {
  options?: MembershipPlanOption[];
  custom_fields?: MembershipCustomField[];
};

const props = defineProps<{
  plan: MembershipPlanPublic;
  allPlans: SiblingPlan[];
  hasMultiplePlans: boolean;
  planQuantities: Record<string, number>;
  quantity: number;
  maxQuantity: number;
  maxQuantityPerPlan: number;
  remainingPlaces: number | null;
  durationLabel: string;
  isFlexiblePricing: boolean;
  isFreePricing: boolean;
  flexiblePrice: number | null;
  planPrice: number;
  planOptions: MembershipPlanOption[];
  customFields: MembershipCustomField[];
  firstParticipant: ParticipantLocal;
  isFreeOrder: boolean;
  totalPrice: number;
  canGoFromStep1: boolean;
  isFileUploading: (fieldId: string, idx: number) => boolean;
  isFileUploaded: (fieldId: string, idx: number) => boolean;
  getFileUploadError: (fieldId: string, idx: number) => string | undefined;
}>();

const emit = defineEmits<{
  // `id` = planId. Les options se choisissent à l'étape « Participants ».
  (e: 'increment-plan' | 'decrement-plan', id: string): void;
  (e: 'increment' | 'decrement' | 'build-participants' | 'continue'): void;
  (e: 'update:flexible-price', v: number | null): void;
  (e: 'upload-file', file: File, fieldId: string, idx: number): void;
  (e: 'remove-file', fieldId: string, idx: number): void;
}>();

// Two-way binding for flexible price (input v-model)
const flexiblePriceLocal = computed({
  get: () => props.flexiblePrice,
  set: (v) => emit('update:flexible-price', v),
});


// Places restantes PAR TARIF (parité billetterie BslugStepTickets) : dérivé de
// `max_members - member_count`, borné à 0. `null` = pas de limite → rien affiché.
// Karim 2026-07-08.
function planRemaining(ap: SiblingPlan): number | null {
  if (!ap.max_members) return null;
  return Math.max(0, ap.max_members - (ap.member_count || 0));
}
// Tarif complet : limite atteinte (0 place restante). Distinct de `closed`
// (fermé manuellement par l'orga).
function isPlanFull(ap: SiblingPlan): boolean {
  const r = planRemaining(ap);
  return r !== null && r <= 0;
}

</script>

<template>
  <div>
    <!-- Multi-plan: list all plans with individual quantity selectors -->
    <template v-if="hasMultiplePlans">
      <div
        v-for="(ap, apIdx) in allPlans"
        :key="ap.id"
        class="px-4 md:px-6 py-4 md:py-5"
        :class="[
          apIdx < allPlans.length - 1 ? 'border-b border-gray-100 dark:border-gray-800' : '',
          tarifAdhesionFerme(ap) ? 'opacity-60' : '',
        ]"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <p class="text-sm font-semibold text-[#2d2e5c] dark:text-white">{{ nomDuTarif(ap) }}</p>
              <!-- « Sélectionné » = choisi par la personne (quantité > 0),
                   plus le tarif de l'adresse de la page (Karim 2026-10-29). -->
              <span
                v-if="(planQuantities[ap.id] || 0) > 0 && !tarifAdhesionFerme(ap)"
                class="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
              >
                {{ $t('adhesion.aslugStepPlans.selectionne') }}
              </span>
              <!-- Tarif clôturé : badge + grisage + non sélectionnable.
                   Karim 2026-06-27. -->
              <span
                v-if="ap.status === 'closed'"
                class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
              >
                <UIcon name="i-lucide-lock" class="size-2.5" />
                {{ $t('adhesion.aslugStepPlans.cloture') }}
              </span>
              <!-- Saison terminée (Karim 2026-10-09) : grisé, non
                   sélectionnable, comme un tarif clôturé. -->
              <span
                v-else-if="saisonDuTarifTerminee(ap)"
                class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
              >
                <UIcon name="i-lucide-calendar-x" class="size-2.5" />
                {{ $t('adhesion.aslugStepPlans.saisonTerminee') }}
              </span>
            </div>
            <!-- eslint-disable vue/no-v-html -->
            <!-- Reason: HTML tags stripped, plain text only (surrogate-safe) -->
            <!-- `tarif_description` (migration 232) et NON `description` : cette
                 dernière porte la description de la PAGE sur la ligne leader,
                 qui s'affichait donc en double — une fois dans l'en-tête, une
                 fois sous le tarif. Repli sur `description` pour les tarifs
                 secondaires non réenregistrés depuis la migration. -->
            <p
              v-if="ap.tarif_description || (ap.id !== ap.plan_group_id && ap.description)"
              class="text-xs text-[#717198] dark:text-gray-400 mt-0.5 line-clamp-2"
              v-html="planDescriptionPreview(ap.tarif_description || ap.description || '')"
            />
            <!-- eslint-enable vue/no-v-html -->
            <!-- Places restantes PAR TARIF (parité billetterie). Rouge sous
                 10 places. « Complet » si limite atteinte (≠ clôturé). Rien si
                 pas de limite (max_members absent). Karim 2026-07-08. -->
            <p
              v-if="!tarifAdhesionFerme(ap) && isPlanFull(ap)"
              class="text-[11px] font-semibold text-red-500 mt-1"
            >
              {{ $t('adhesion.aslugStepPlans.complet') }}
            </p>
            <p
              v-else-if="!tarifAdhesionFerme(ap) && planRemaining(ap) !== null"
              class="text-[11px] mt-1"
              :class="planRemaining(ap)! <= 10 ? 'text-red-500 font-medium' : 'text-[#717198] dark:text-gray-500'"
            >
              {{ $t('adhesion.aslugStepPlans.placeRestantePluriel', planRemaining(ap)!) }}
            </p>
            <div class="mt-1.5 md:hidden">
              <span
                class="text-sm font-bold"
                :class="Number(ap.price) === 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-[#2d2e5c] dark:text-white'"
              >
                {{ Number(ap.price) === 0 ? $t('adhesion.aslugStepPlans.gratuit') : formatCurrency(Number(ap.price)) }}
              </span>
            </div>
          </div>
          <div class="hidden md:block w-[140px] text-right shrink-0 mr-4">
            <span
              class="text-base font-bold"
              :class="Number(ap.price) === 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-[#2d2e5c] dark:text-white'"
            >
              {{ Number(ap.price) === 0 ? $t('adhesion.aslugStepPlans.gratuit') : formatCurrency(Number(ap.price)) }}
            </span>
          </div>
          <!-- Tarif clôturé ou saison terminée : pas de sélecteur de
               quantité (non achetable). -->
          <div
            v-if="tarifAdhesionFerme(ap)"
            class="flex items-center shrink-0 text-[11px] font-medium text-amber-600 dark:text-amber-400"
          >
            <UIcon :name="ap.status === 'closed' ? 'i-lucide-lock' : 'i-lucide-calendar-x'" class="size-4" />
          </div>
          <div v-else class="flex items-center gap-1.5 md:gap-2 shrink-0">
            <button
              :data-testid="ap.id === plan?.id ? 'plan-quantity-decrement' : `plan-quantity-decrement-${ap.id}`"
              class="w-8 h-8 rounded-xl border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-30"
              :disabled="(planQuantities[ap.id] || 0) <= 0"
              @click="$emit('decrement-plan', ap.id)"
            >
              <UIcon name="i-lucide-minus" class="size-3.5" />
            </button>
            <span :data-testid="ap.id === plan?.id ? 'plan-quantity-value' : `plan-quantity-value-${ap.id}`" class="w-6 text-center text-sm font-bold text-[#2d2e5c] dark:text-white">{{ planQuantities[ap.id] || 0 }}</span>
            <button
              :data-testid="ap.id === plan?.id ? 'plan-quantity-increment' : `plan-quantity-increment-${ap.id}`"
              class="w-8 h-8 rounded-xl border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-30"
              :disabled="(planQuantities[ap.id] || 0) >= maxQuantityPerPlan || (planRemaining(ap) !== null && (planQuantities[ap.id] || 0) >= planRemaining(ap)!)"
              @click="$emit('increment-plan', ap.id)"
            >
              <UIcon name="i-lucide-plus" class="size-3.5" />
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Single plan: original layout -->
    <template v-else>
      <div class="px-4 md:px-6 py-4 md:py-5 border-b border-gray-100 dark:border-gray-800">
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-[#2d2e5c] dark:text-white">{{ plan.title }}</p>
            <p v-if="durationLabel" class="text-xs text-[#717198] dark:text-gray-400 mt-0.5">
              {{ $t('adhesion.aslugStepPlans.duree', { duree: durationLabel }) }}
            </p>
            <div class="flex items-center gap-2 mt-1">
              <span v-if="remainingPlaces !== null" class="text-[11px] text-[#717198] dark:text-gray-500">
                {{ $t('adhesion.aslugStepPlans.placesRestantes', { n: remainingPlaces }) }}
              </span>
            </div>
            <div class="mt-1.5 md:hidden">
              <span
                class="text-sm font-bold"
                :class="planPrice === 0 && !isFlexiblePricing ? 'text-emerald-600 dark:text-emerald-400' : 'text-[#2d2e5c] dark:text-white'"
              >
                <template v-if="isFreePricing">{{ $t('adhesion.aslugStepPlans.gratuit') }}</template>
                <template v-else-if="isFlexiblePricing">{{ $t('adhesion.aslugStepPlans.tarifLibre') }}</template>
                <template v-else-if="planPrice === 0">{{ $t('adhesion.aslugStepPlans.gratuit') }}</template>
                <template v-else>{{ formatCurrency(planPrice) }}</template>
              </span>
            </div>
          </div>
          <div class="hidden md:block w-[140px] text-right shrink-0 mr-4">
            <span
              class="text-base font-bold"
              :class="planPrice === 0 && !isFlexiblePricing ? 'text-emerald-600 dark:text-emerald-400' : 'text-[#2d2e5c] dark:text-white'"
            >
              <template v-if="isFreePricing">{{ $t('adhesion.aslugStepPlans.gratuit') }}</template>
              <template v-else-if="isFlexiblePricing">{{ $t('adhesion.aslugStepPlans.tarifLibre') }}</template>
              <template v-else-if="planPrice === 0">{{ $t('adhesion.aslugStepPlans.gratuit') }}</template>
              <template v-else>{{ formatCurrency(planPrice) }}</template>
            </span>
          </div>
          <div class="flex items-center gap-1.5 md:gap-2 shrink-0">
            <button
              data-testid="plan-quantity-decrement"
              class="w-8 h-8 rounded-xl border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-30"
              :disabled="quantity <= 1"
              @click="$emit('decrement')"
            >
              <UIcon name="i-lucide-minus" class="size-3.5" />
            </button>
            <span data-testid="plan-quantity-value" class="w-6 text-center text-sm font-bold text-[#2d2e5c] dark:text-white">{{ quantity }}</span>
            <button
              data-testid="plan-quantity-increment"
              class="w-8 h-8 rounded-xl border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-30"
              :disabled="quantity >= maxQuantity"
              @click="$emit('increment')"
            >
              <UIcon name="i-lucide-plus" class="size-3.5" />
            </button>
          </div>
        </div>
      </div>

      <!-- Tarif libre : saisie du montant -->
      <div v-if="isFlexiblePricing" class="px-4 md:px-6 py-4 md:py-5 border-b border-gray-100 dark:border-gray-800">
        <label class="block text-sm font-semibold text-[#2d2e5c] dark:text-white mb-2">
          {{ $t('adhesion.aslugStepPlans.choisirMontant') }}
        </label>
        <div class="flex items-center gap-2">
          <div class="relative flex-1">
            <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-[#717198] dark:text-gray-400 pointer-events-none">€</span>
            <input
              v-model.number="flexiblePriceLocal"
              type="number"
              :min="plan?.min_price ?? 0"
              :max="plan?.max_price ?? undefined"
              step="0.01"
              placeholder="0.00"
              class="w-full h-11 pl-8 pr-3 rounded-xl border border-gray-200 dark:border-[#262626] bg-white dark:bg-[#0f0f0f] text-base font-semibold text-[#2d2e5c] dark:text-white focus:outline-none focus:border-[#212121] dark:focus:border-white"
            />
          </div>
        </div>
        <p class="text-[11px] text-[#717198] dark:text-gray-500 mt-2">
          <span v-if="plan?.min_price && plan?.max_price">
            {{ $t('adhesion.aslugStepPlans.entreMontants', { min: formatCurrency(Number(plan.min_price)), max: formatCurrency(Number(plan.max_price)) }) }}
          </span>
          <span v-else-if="plan?.min_price">
            {{ $t('adhesion.aslugStepPlans.minimumMontant', { min: formatCurrency(Number(plan.min_price)) }) }}
          </span>
          <span v-else-if="plan?.max_price">
            {{ $t('adhesion.aslugStepPlans.maximumMontant', { max: formatCurrency(Number(plan.max_price)) }) }}
          </span>
          <span v-else>{{ $t('adhesion.aslugStepPlans.montantLibreAssociation') }}</span>
        </p>
      </div>

      <!-- Hint: options/custom fields available in next step (multi-member) -->
      <div v-if="quantity > 0 && (planOptions.filter((o: MembershipPlanOption) => o.is_active).length > 0 || customFields.length > 0)" class="px-4 md:px-6 py-3 border-b border-gray-100 dark:border-gray-800">
        <p class="text-[11px] text-[#717198] dark:text-gray-500 flex items-center gap-1">
          <UIcon name="i-lucide-plus-circle" class="size-3" />
          {{ $t('adhesion.aslugStepPlans.optionsEtapeSuivante') }}
        </p>
      </div>

      <!-- Options et champs personnalisés RETIRÉS d'ici (Karim 2026-09-21) :
           ils vivent désormais à l'étape « Participants », qui est toujours
           affichée — y compris pour une seule adhésion. Les laisser aussi ici
           les aurait fait apparaître à deux endroits. -->
    </template>

    <!-- Hint: options/custom fields available in next step (multi-plan or multi-member) -->
    <div v-if="hasMultiplePlans && quantity > 0" class="px-4 md:px-6 py-3 border-b border-gray-100 dark:border-gray-800">
      <p class="text-[11px] text-[#717198] dark:text-gray-500 flex items-center gap-1">
        <UIcon name="i-lucide-plus-circle" class="size-3" />
        {{ $t('adhesion.aslugStepPlans.optionsEtapeSuivante') }}
      </p>
    </div>

    <!-- Summary + Continue -->
    <div class="flex items-center justify-between px-4 md:px-6 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-[#0f0f0f]">
      <div>
        <span class="font-bold text-base md:text-lg text-[#2d2e5c] dark:text-white">
          {{ isFreeOrder ? $t('adhesion.aslugStepPlans.gratuit') : formatCurrency(totalPrice) }}
        </span>
        <span v-if="quantity > 1" class="text-xs text-[#717198] dark:text-gray-400 ml-2">
          ({{ $t('adhesion.aslugStepPlans.adhesionPluriel', quantity) }})
        </span>
      </div>
      <button
        data-testid="btn-step1-continue"
        class="px-6 md:px-8 py-2.5 md:py-3 accent-btn rounded-xl text-sm font-semibold hover:bg-[#333] dark:hover:bg-gray-100 transition-colors disabled:opacity-40"
        :disabled="!canGoFromStep1"
        @click="$emit('continue')"
      >
        {{ $t('commun.actions.continuer') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Boutons d'action — couleur choisie dans la configuration de l'adhésion.
   Repli #212121 quand aucune couleur n'est posée. Même classe et mêmes
   variables que la billetterie (BslugStepRecap) : une seule règle pour les
   trois domaines. Karim 2026-09-12. */
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
.accent-btn:hover {
  opacity: 0.9;
}
</style>
